import Papa from 'papaparse';
import {
    choicesOf,
    CsvImportConfig,
    ImportAction,
    ImportColumn,
    ImportLookups,
    ImportMode,
    ImportOption,
    ImportProblems,
    ImportResult,
    ImportRow,
    ImportValues,
    ROW_PROBLEM,
} from './csv-import-types';

/**
 * The pure half of the CSV importers: reading a file into rows, checking rows, writing files.
 * HQ-API checks everything again on Validate and Import; these checks are only there so the
 * admin sees most problems while editing, without a round trip.
 */

const ID_PATTERN: RegExp = /^[1-9][0-9]{0,9}$/;

/**
 * The optional first column of every importer. A row with an id updates that record, the only
 * way to change what identifies it (its address, email or name); a row without one matches on
 * those. Ids differ between Dev and production.
 */
export const ID_COLUMN: ImportColumn = {
    key: 'id',
    label: 'Id',
    kind: 'text',
    width: 80,
    check: (value: string): string | null => (ID_PATTERN.test(value) ? null : 'Must be a record id (a whole number).'),
    helper: 'Leave empty to add a record, or to match one by its own details. Fill it to update that record, '
        + 'including a change of address, email or name.',
};

/** Header text compared without case, spaces or punctuation, so "Solo or Company" finds `soloOrCompany`. */
export function headerKey(header: string): string {
    return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export interface ParsedFile {
    rows: ImportRow[];
    /** Headers in the file that match no column; their cells are dropped. */
    ignoredHeaders: string[];
    /** Columns the file has no header for; their cells start empty. */
    missingColumns: string[];
    /** Problems with the file itself, such as an unclosed quote. */
    parseErrors: string[];
}

/**
 * Turn parsed CSV records into grid rows. Split from `parseCsvFile` so it can be tested without
 * a `File`.
 */
export function rowsFromRecords(
    config: CsvImportConfig,
    headers: string[],
    records: Record<string, string>[],
): Omit<ParsedFile, 'parseErrors'> {
    const byHeader: Map<string, ImportColumn> = new Map();
    for (const column of config.columns) {
        byHeader.set(headerKey(column.key), column);
        byHeader.set(headerKey(column.label), column);
    }

    const mapping: Map<string, string> = new Map();
    const ignoredHeaders: string[] = [];
    for (const header of headers) {
        const column: ImportColumn | undefined = byHeader.get(headerKey(header));
        if (column && ![...mapping.values()].includes(column.key)) {
            mapping.set(header, column.key);
        } else if (header.trim() !== '') {
            ignoredHeaders.push(header);
        }
    }
    const mapped: string[] = [...mapping.values()];
    // A file with no id column is the usual case, not a gap worth a notice.
    const missingColumns: string[] = config.columns
        .filter((column: ImportColumn): boolean => column.key !== ID_COLUMN.key && !mapped.includes(column.key))
        .map((column: ImportColumn): string => column.key);

    const rows: ImportRow[] = records.map((record: Record<string, string>, index: number): ImportRow => {
        const values: ImportValues = emptyValues(config);
        for (const [header, key] of mapping) {
            values[key] = normaliseCell(config.columns.find((c: ImportColumn): boolean => c.key === key), record[header]);
        }

        return { rowId: index + 1, values: values };
    });

    return { rows: rows, ignoredHeaders: ignoredHeaders, missingColumns: missingColumns };
}

export function parseCsvFile(config: CsvImportConfig, file: File): Promise<ParsedFile> {
    return new Promise((resolve: (parsed: ParsedFile) => void): void => {
        Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: 'greedy',
            complete: (result: Papa.ParseResult<Record<string, string>>): void => {
                const parsed = rowsFromRecords(config, result.meta.fields ?? [], result.data);
                resolve({
                    ...parsed,
                    parseErrors: parseNotices(result.errors),
                });
            },
            error: (error: Error): void => {
                resolve({ rows: [], ignoredHeaders: [], missingColumns: [], parseErrors: [error.message] });
            },
        });
    });
}

/**
 * The parser's problems as notices. A record with too few fields still loads and the grid shows
 * what landed where, which says more than the parser's own wording. One with too many has lost
 * its extra cells, and when the last column is long text (a template's html) nothing else would
 * show it: a quoted cell with a space before its opening quote is split at every comma inside it.
 */
export function parseNotices(errors: Papa.ParseError[]): string[] {
    return errors
        .filter((error: Papa.ParseError): boolean => error.code !== 'TooFewFields')
        .map((error: Papa.ParseError): string => {
            const row: string = error.row !== undefined ? 'Row ' + (error.row + 1) : 'A row';
            if (error.code === 'TooManyFields') {
                return row + ' has more cells than the header, so its last cells were dropped. A quoted cell '
                    + 'may have been split at its commas: check nothing, not even a space, comes between a '
                    + 'comma and the opening quote.';
            }

            return row + ': ' + error.message;
        });
}

export function emptyValues(config: CsvImportConfig): ImportValues {
    const values: ImportValues = {};
    for (const column of config.columns) {
        values[column.key] = '';
    }

    return values;
}

/**
 * Tidy a cell as it loads: trim it, put a choice in its canonical case so the dropdown shows it,
 * and write a list of ids with semicolons. Anything unrecognised is kept as typed, so the
 * problem shows against it rather than the value vanishing.
 */
export function normaliseCell(column: ImportColumn | undefined, raw: unknown): string {
    let value: string = typeof raw === 'string' ? raw : (raw === undefined || raw === null ? '' : String(raw));
    // Long text keeps its inner line breaks; only the ends are trimmed.
    value = unwrapQuotes(value.trim());
    if (!column) {
        return value;
    }
    if (column.kind === 'boolean') {
        const lower: string = value.toLowerCase();
        if (['yes', 'y', 'true', '1'].includes(lower)) {
            return 'yes';
        }
        if (['no', 'n', 'false', '0'].includes(lower)) {
            return 'no';
        }

        return value;
    }
    if (column.kind === 'choice' && column.choices) {
        const match = column.choices.find((choice): boolean => choice.value.toLowerCase() === value.toLowerCase());

        return match ? match.value : value;
    }
    // A cell that may hold names keeps its commas: a name can contain one.
    if (column.kind === 'references' && !column.acceptsNames) {
        return splitIds(value).join(';');
    }

    return value;
}

/**
 * A cell wrapped whole in one pair of double quotes, straight or curly, without them. A spreadsheet
 * saves a value typed with its own quotes as `"""Acme Ltd"""`, which reads back as `"Acme Ltd"`,
 * and that stored 697 template names inside quotes (2026-10-19). A cell with any other double quote
 * (`"A" and "B"`) is left alone. HQ-API's `ImportRow::withoutWrappingQuotes()` does the same.
 */
export function unwrapQuotes(value: string): string {
    const match: RegExpMatchArray | null = value.match(/^\s*["\u201C\u201D]([^"\u201C\u201D]*)["\u201C\u201D]\s*$/);

    return match ? match[1].trim() : value;
}

export function splitIds(value: string): string[] {
    return value.split(/[;,]/).map((part: string): string => part.trim()).filter((part: string): boolean => part !== '');
}

/** A name compared without case, every run of spaces as one, a comma always followed by one space. */
function nameKey(name: string): string {
    return name.trim().replace(/\s*,\s*/g, ', ').replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The ids in a cell of ids and names, as HQ-API's `NokEmailTemplateImporter::industryIds()` reads
 * it. A piece that is neither an id nor a name is joined with the pieces after it, longest first,
 * since a name can hold a comma ("Media, marketing & advertising").
 */
export function resolveReferences(value: string, options: ImportOption[]): { ids: string[], unknown: string[] } {
    const pieces: string[] = splitIds(value);
    const byName: Map<string, string> = new Map(
        options.filter((option: ImportOption): boolean => !!option.name)
            .map((option: ImportOption): [string, string] => [nameKey(option.name ?? ''), option.value]),
    );
    const ids: string[] = [];
    const unknown: string[] = [];
    for (let i: number = 0; i < pieces.length; i++) {
        if (ID_PATTERN.test(pieces[i])) {
            if (options.some((option: ImportOption): boolean => option.value === pieces[i])) {
                ids.push(pieces[i]);
            } else {
                unknown.push(pieces[i]);
            }
            continue;
        }
        let matched: boolean = false;
        for (let j: number = pieces.length - 1; j >= i; j--) {
            const id: string | undefined = byName.get(nameKey(pieces.slice(i, j + 1).join(', ')));
            if (id !== undefined) {
                ids.push(id);
                i = j;
                matched = true;
                break;
            }
        }
        if (!matched) {
            unknown.push(pieces[i]);
        }
    }

    return { ids: [...new Set(ids)], unknown };
}

/**
 * Turn the names in `acceptsNames` cells into ids, so the grid's dropdowns show them. A cell with
 * anything unrecognised is left as typed, so its problem shows against what the file said.
 */
export function resolveNamedCells(config: CsvImportConfig, rows: ImportRow[], lookups: ImportLookups): ImportRow[] {
    const columns: ImportColumn[] = config.columns.filter((column: ImportColumn): boolean =>
        column.kind === 'references' && !!column.acceptsNames && !!column.lookup && (lookups[column.lookup] ?? []).length > 0);
    if (columns.length === 0) {
        return rows;
    }

    return rows.map((row: ImportRow): ImportRow => {
        const values: ImportValues = { ...row.values };
        for (const column of columns) {
            const { ids, unknown } = resolveReferences(values[column.key] ?? '', lookups[column.lookup ?? ''] ?? []);
            if (unknown.length === 0) {
                values[column.key] = ids.join(';');
            }
        }

        return { ...row, values };
    });
}

export function isRequired(column: ImportColumn, values: ImportValues): boolean {
    return typeof column.required === 'function' ? column.required(values) : column.required === true;
}

/**
 * The problems with one row that can be seen without HQ-API, by column key. Duplicates against
 * the database, and rules that need a record's details, are left to the server.
 *
 * When rows may update stored records only the id is checked here. Whether a row matches a
 * record, and so whether an empty cell keeps a stored value, is HQ-API's to say, and a value an
 * update leaves as it is (an industry deleted since) is not refused there, so the browser would
 * flag what HQ-API accepts and hold back Import.
 */
export function checkRow(
    config: CsvImportConfig,
    values: ImportValues,
    lookups: ImportLookups,
    mode: ImportMode = 'create',
): Record<string, string> {
    const problems: Record<string, string> = {};
    const id: string = (values[ID_COLUMN.key] ?? '').trim();
    if (mode === 'create' && id !== '') {
        return { [ID_COLUMN.key]: 'Leave the id empty to add a new record, or choose Add and update.' };
    }
    if (mode !== 'create') {
        const failed: string | null | undefined = id === '' ? null : ID_COLUMN.check?.(id);

        return failed ? { [ID_COLUMN.key]: failed } : {};
    }
    for (const column of config.columns) {
        const value: string = (values[column.key] ?? '').trim();
        if (value === '') {
            if (isRequired(column, values)) {
                problems[column.key] = 'Required.';
            }
            continue;
        }
        if (column.maxLength && value.length > column.maxLength) {
            problems[column.key] = 'Must be ' + column.maxLength + ' characters or fewer.';
            continue;
        }
        const choices = choicesOf(column);
        if (choices && !choices.some((choice): boolean => choice.value.toLowerCase() === value.toLowerCase())) {
            problems[column.key] = 'Must be one of: ' + choices.map((choice): string => choice.value).join(', ') + '.';
            continue;
        }
        const failed: string | null | undefined = column.check?.(value);
        if (failed) {
            problems[column.key] = failed;
            continue;
        }
        if (column.kind === 'references' && column.acceptsNames) {
            // Without the list nothing can be told apart from a typo; HQ-API checks on Validate.
            const options = column.lookup ? lookups[column.lookup] : undefined;
            const unknown: string[] = options && options.length > 0 ? resolveReferences(value, options).unknown : [];
            if (unknown.length > 0) {
                problems[column.key] = 'No record with the id or name: ' + unknown.join(', ') + '.';
            }
            continue;
        }
        if (column.kind === 'reference' || column.kind === 'references') {
            const ids: string[] = column.kind === 'reference' ? [value] : splitIds(value);
            const malformed: string | undefined = ids.find((id: string): boolean => !ID_PATTERN.test(id));
            if (malformed !== undefined) {
                problems[column.key] = '"' + malformed + '" is not a record id.';
                continue;
            }
            const options = column.lookup ? lookups[column.lookup] : undefined;
            const unknown: string[] = options
                ? ids.filter((id: string): boolean => !options.some((option): boolean => option.value === id))
                : [];
            if (unknown.length > 0) {
                problems[column.key] = 'No record with id ' + unknown.join(', ') + '.';
            }
        }
    }

    return problems;
}

/**
 * Pin HQ-API's problems to grid rows. Its `row` is the position in the batch that was sent,
 * which is the order `rows` was in when it was sent.
 */
export function problemsFromResult(result: ImportResult, sentRowIds: number[]): ImportProblems {
    const problems: ImportProblems = {};
    for (const error of result.errors ?? []) {
        const rowId: number | undefined = sentRowIds[error.row];
        if (rowId === undefined) {
            continue;
        }
        problems[rowId] = problems[rowId] ?? {};
        const field: string = error.field ?? ROW_PROBLEM;
        problems[rowId][field] = problems[rowId][field]
            ? problems[rowId][field] + ' ' + error.message
            : error.message;
    }

    return problems;
}

/**
 * HQ-API's per-row actions by grid row id. Like its problems, they name rows by their position in
 * the batch that was sent.
 */
export function actionsFromResult(result: ImportResult, sentRowIds: number[]): Record<number, ImportAction> {
    const actions: Record<number, ImportAction> = {};
    for (const entry of result.actions ?? []) {
        const rowId: number | undefined = sentRowIds[entry.row];
        if (rowId !== undefined) {
            actions[rowId] = entry.action;
        }
    }

    return actions;
}

/** Only the config's columns, in its order: what HQ-API is sent and what a download writes. */
export function rowPayload(config: CsvImportConfig, values: ImportValues): ImportValues {
    const payload: ImportValues = {};
    for (const column of config.columns) {
        payload[column.key] = values[column.key] ?? '';
    }

    return payload;
}

export function toCsv(config: CsvImportConfig, rows: ImportValues[]): string {
    const fields: string[] = config.columns.map((column: ImportColumn): string => column.key);

    return Papa.unparse({
        fields: fields,
        data: rows.map((values: ImportValues): string[] => fields.map((key: string): string => values[key] ?? '')),
    }, { newline: '\r\n' });
}

/** HQ-API's exported rows as a CSV, in the columns it named. */
export function exportCsv(columns: string[], rows: ImportValues[]): string {
    return Papa.unparse({
        fields: columns,
        data: rows.map((values: ImportValues): string[] => columns.map((key: string): string => values[key] ?? '')),
    }, { newline: '\r\n' });
}

export function downloadCsv(fileName: string, csv: string): void {
    // The byte order mark is for Excel, which otherwise reads UTF-8 as the local code page and
    // turns every accented name into two characters. `headerKey` ignores it on the way back in.
    const blob: Blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url: string = URL.createObjectURL(blob);
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}
